import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, getFeaturedArticles } from '@/mocks/articles';
import { categoriesMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleById(slug || '');
  const relatedArticles = getFeaturedArticles().filter((a) => a.id !== slug).slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel = categoriesMap[article.category]?.name || '综合';
  const categorySlug = article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    url: `https://maishiranui-guide.com/article/${article.id}`,
    inLanguage: 'zh-CN',
    articleSection: categoryLabel,
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel, href: `/category/${categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${categorySlug}`}
                  className="text-xs font-medium text-primary-600 bg-primary-100/70 px-3 py-1.5 rounded-full hover:bg-primary-200/70 transition-colors"
                >
                  {categoryLabel}
                </Link>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
              </div>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden aspect-[16/9]">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom text-foreground-800 leading-relaxed">
              {article.content.map((block, index) => {
                if (block.type === 'heading' && block.text) {
                  const Tag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
                  const headingClasses: Record<number, string> = {
                    2: 'font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4',
                    3: 'font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3',
                  };
                  return (
                    <Tag key={index} className={headingClasses[block.level || 2] || headingClasses[2]}>
                      {block.text}
                    </Tag>
                  );
                }
                if (block.type === 'paragraph' && block.text) {
                  return (
                    <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'list' && block.items) {
                  return (
                    <ul key={index} className="space-y-2 mb-6 pl-5">
                      {block.items.map((item, i) => (
                        <li key={i} className="text-sm md:text-base text-foreground-700 flex items-start gap-2">
                          <span className="text-primary-500 mt-1 flex-shrink-0">
                            <i className="ri-checkbox-circle-line text-sm"></i>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === 'tip' && block.text) {
                  return (
                    <div key={index} className="my-6 p-5 rounded-xl bg-accent-100/70 border border-accent-300/60">
                      <p className="text-sm text-accent-800 leading-relaxed">
                        <i className="ri-lightbulb-line mr-2"></i>
                        {block.text}
                      </p>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full bg-background-200/70 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full bg-background-100 py-12 md:py-16">
              <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}